import React, { forwardRef, ReactNode } from "react";
// import HCaptcha from "@hcaptcha/react-hcaptcha";
export type FormErrorState = Record<string, string[] | undefined> | null;
/* jscpd:ignore-start */
import { customSanitize } from "@/utils/customSanitize";
import { ListPropsV2, ListV2 } from "@/components/Listbox/Listbox";

import styles from "../Form.module.scss";

type CommonProps = {
  label?: string;
  errors?: FormErrorState;
  required_label?: string;
};

type InputProps = {
  children?: React.ReactNode;
  subline?: string;
  inputType:
    | "number"
    | "text"
    | "checkbox"
    | "tel"
    | "url"
    | "email"
    | "textarea"
    | "date";
  label_checkbox?: string;
  required_label?: string;
  checked?: boolean;
} & CommonProps &
  React.InputHTMLAttributes<HTMLInputElement>;

interface LabelProps {
  Tag?: "label" | "p" | "legend";
  label?: string;
  required: boolean | undefined;
  required_label?: string;
  htmlFor?: string;
}

export const LabelV2 = ({
  label,
  required,
  required_label,
  htmlFor,
  Tag = "label",
}: LabelProps) => {
  return (
    <Tag className={styles["label"] + " label"} htmlFor={htmlFor}>
      {label && label}
      {required && required_label && (
        <span className={styles["required-text"] + " required-text"}>
          {" "}
          ({required_label})
        </span>
      )}
    </Tag>
  );
};

// TODO: extend this component to support more input types
// This component is currently used for inputs with type: text, checkbox, email, textarea
export const InputV2 = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  InputProps
>(
  (
    {
      errors,
      label,
      subline,
      inputType,
      required_label,
      required,
      className = "",
      ...props
    },
    ref,
  ) => {
    const id = `input-${props.name}`;
    return (
      <FieldWrapper
        errors={errors}
        name={props.name}
        className={`w-full ${className} ${styles["input-group-formbuilder"]}`}
      >
        <LabelV2
          label={label}
          required={required}
          required_label={required_label}
          htmlFor={id}
        />
        {(inputType === "text" ||
          inputType === "email" ||
          inputType === "number" ||
          inputType === "tel" ||
          inputType === "url" ||
          inputType === "date") && (
          <>
            <input
              id={id}
              {...props}
              type={inputType}
              ref={ref as React.Ref<HTMLInputElement>}
              className={`${styles["input"]} placeholder`}
              required={required}
            />
          </>
        )}

        {subline && <span className={styles["subline"]}>{subline}</span>}
      </FieldWrapper>
    );
  },
);

InputV2.displayName = "InputV2";

type TextAriaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> &
  CommonProps;

export const TextAreaV2 = ({
  errors,
  label,
  required_label,
  required,
  className = "",
  ...props
}: TextAriaProps) => {
  const id = `input-${props.name}`;

  return (
    <FieldWrapper
      errors={errors}
      name={props.name}
      className={`w-full ${className}`}
    >
      <LabelV2
        label={label}
        required={required}
        required_label={required_label}
        htmlFor={id}
      />
      <textarea
        {...props}
        id={id}
        className={`${styles["input"]} placeholder`}
        required={required}
        rows={6}
      />{" "}
      <span className={styles["bar-text-area"]}></span>
    </FieldWrapper>
  );
};

export const CheckboxV2 = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      errors,
      label,
      required_label,
      required,
      label_checkbox,
      className = "",
      inputType: _ /* otherwise it gets passed onto the input */,
      checked,
      ...props
    },
    ref,
  ) => {
    const id = `input-` + props.name;
    const checkedProps = typeof checked === "boolean" ? { checked } : {};

    return (
      <FieldWrapper
        errors={errors}
        name={props.name}
        className={`${className}`}
      >
        <fieldset>
          <LabelV2
            Tag="legend"
            label={label}
            required={required}
            required_label={required_label}
          />
          <div className={"input-checkbox"}>
            <input
              {...props}
              {...checkedProps}
              ref={ref as React.Ref<HTMLInputElement>}
              type="checkbox"
              id={id}
              required={required}
            />
            {label_checkbox && (
              /* eslint-disable-next-line jsx-a11y/label-has-associated-control */
              <label
                dangerouslySetInnerHTML={{
                  __html: customSanitize(label_checkbox),
                }}
                htmlFor={id}
                className="mb-0 pb-0"
              />
            )}
          </div>
        </fieldset>
      </FieldWrapper>
    );
  },
);

CheckboxV2.displayName = "CheckboxV2";

type RadioProps = React.InputHTMLAttributes<HTMLInputElement> &
  CommonProps & {
    options: {
      label: string;
      value: string;
      checked?: boolean;
    }[];
  };

export const RadioV2 = ({
  options,
  errors,
  label,
  className,
  required_label,
  required,
  ...props
}: RadioProps) => {
  return (
    <FieldWrapper errors={errors} name={props.name} className={`${className}`}>
      <LabelV2
        Tag="p"
        label={label}
        required={required}
        required_label={required_label}
      />

      <div className={"input-radio"}>
        {options.map((o) => {
          const radioId = `${props.name}-${o.value}`;

          return (
            <div className="is-flex is-align-items-center mb-2" key={radioId}>
              <input
                {...props}
                checked={o.checked}
                onChange={(e) => {
                  props.onChange?.(e);
                }}
                type="radio"
                id={radioId}
                value={o.value}
              />
              <label
                className={`${styles["label"]} has-text-font-regular mb-0 pb-0`}
                htmlFor={radioId}
              >
                {o.label}
              </label>
            </div>
          );
        })}
      </div>
    </FieldWrapper>
  );
};

RadioV2.displayName = "RadioV2";

type SelectProps = CommonProps &
  React.InputHTMLAttributes<HTMLSelectElement> & {
    errors?: FormErrorState;
    children?: React.ReactNode;
    items: ListPropsV2["items"];
    setSelected: ListPropsV2["setSelected"];
    selected?: string;
    placeholder?: string;
  };

export const SelectV2 = ({
  className,
  errors,
  items,
  selected,
  setSelected,
  label,
  required_label,
  required,
  placeholder,
  ...props
}: SelectProps) => {
  const id = "select-" + props.name;
  return (
    <FieldWrapper errors={errors} name={props.name} className={`${className}`}>
      <LabelV2
        htmlFor={id}
        label={label}
        required={required}
        required_label={required_label}
      />
      <ListV2
        className="w-100 input-dropdown"
        buttonId={id}
        items={items}
        setSelected={setSelected}
        selected={selected}
        placeholder={placeholder}
      />
    </FieldWrapper>
  );
};

interface FieldWrapperProps {
  errors: FormErrorState | undefined;
  name: string | undefined;
  children: ReactNode;
  className?: string;
}

export const FieldWrapper = ({
  errors,
  name,
  children,
  className,
}: FieldWrapperProps) => {
  const hasError = errors && name && errors[name];

  return (
    <div
      data-field-name={name}
      className={`${styles["input"]} ${hasError ? "has-error" : ""} ${className}`}
    >
      {children}
      {hasError && (
        <p className={"error"}>
          {(errors?.[name!]?.filter(Boolean) ?? []).join(", ")}
        </p>
      )}
    </div>
  );
};
/* jscpd:ignore-end */
